import { useSyncExternalStore } from 'react';

import { CanvasLevel } from '@casa/domain';

/**
 * Where the workspace is looking, kept across a reload.
 *
 * A refresh in the middle of configuring an endpoint should come back to that endpoint, not to
 * the top — losing your place is the same cost as navigating there again. It is remembered per
 * browser rather than in the URL because Kaoto owns the router, and a level is a position in
 * our boards rather than one of its pages.
 */
const REMEMBERED = 'platform.canvas.level';

const remembered = (): CanvasLevel => {
  try {
    const stored = localStorage.getItem(REMEMBERED);
    if (!stored) return { kind: 'uses' };
    const parsed = JSON.parse(stored) as CanvasLevel;
    // a stored level from an older shape would put the boards in a state they cannot draw
    return typeof parsed?.kind === 'string' ? parsed : { kind: 'uses' };
  } catch {
    return { kind: 'uses' };
  }
};

let level: CanvasLevel = remembered();

/**
 * Where the workspace has been, so there is a step back.
 *
 * The breadcrumb says where you are and can climb, but it cannot return: open a tenant from a
 * vendor, then a business, and the only way back to the *previous* place is to rebuild the path
 * by hand. This keeps the trail. It is deliberately not remembered across a reload — a trail
 * from another day is not one anybody is following.
 */
let trail: CanvasLevel[] = [];
const DEPTH = 50;

const listeners = new Set<() => void>();

const announce = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const remember = (next: CanvasLevel) => {
  level = next;
  try {
    localStorage.setItem(REMEMBERED, JSON.stringify(next));
  } catch {
    // a browser refusing storage costs the position on reload, never the navigation itself
  }
  announce();
};

export const canvasLevel = () => level;

export const setCanvasLevel = (next: CanvasLevel): void => {
  /* Standing still is not a step: repeating the same level would need two presses to leave. */
  if (JSON.stringify(next) !== JSON.stringify(level)) {
    trail = [...trail, level].slice(-DEPTH);
  }
  remember(next);
};

export const canvasTrailDepth = (): number => trail.length;

/**
 * The place before this one, or nothing when this is where the session started.
 *
 * It only takes the step off the trail; showing it is the caller's job, because a route level
 * has to be compiled onto Kaoto's canvas rather than merely selected.
 */
export const takeCanvasStepBack = (): CanvasLevel | undefined => {
  const previous = trail[trail.length - 1];
  if (!previous) return undefined;

  trail = trail.slice(0, -1);

  return previous;
};

/**
 * Show a level without recording a step.
 *
 * Used when a level is corrected rather than chosen — walking back along the trail, or climbing
 * out of a board whose subject has been removed. Neither is a place worth returning to.
 */
export const replaceCanvasLevel = (next: CanvasLevel): void => {
  /* Never leave a step that lands where the person already is: that is a press that does nothing. */
  while (trail.length && JSON.stringify(trail[trail.length - 1]) === JSON.stringify(next)) trail = trail.slice(0, -1);
  remember(next);
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

export const useCanvasLevel = () => useSyncExternalStore(subscribe, canvasLevel, canvasLevel);

/** How many steps back there are, so the control can say when there is nowhere to go. */
export const useCanvasTrailDepth = () => useSyncExternalStore(subscribe, canvasTrailDepth, canvasTrailDepth);
